/**
 * ProjectStatusWriter — full-project status report writer (DEV-1296 Slice C).
 *
 * Lightweight vs the mini StatusWriter: dated TipTap narrative + photos, NO KPI
 * coding. Lead/authorized + write/edit-time Journal + feed toggles (the only
 * sanctioned path for a status→Journal post). Reuses StatusPhotoStrip + TipTap.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import StatusPhotoStrip, { type StatusPhotoItem } from '../StatusPhotoStrip/StatusPhotoStrip';
import { getProjectOverride } from '../../Data/ProjectConfig/projectConfigOverrides';
import {
  createProjectStatusReport,
  updateProjectStatusReport,
  deleteProjectStatusReport,
  type ProjectStatusReport,
} from '../../api/projectStatusReports';
import styles from './ProjectStatusWriter.module.css';

interface Props {
  projectGuid: string;
  /** Existing report to edit; null = new. */
  editing?: ProjectStatusReport | null;
  onClose: () => void;
  onSaved: () => void;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ProjectStatusWriter({ projectGuid, editing = null, onClose, onSaved }: Props) {
  const { t } = useAppTranslation('kpi');
  const isEdit = !!editing;

  const [reportingDate, setReportingDate] = useState<string>(editing?.reportingDate || todayIso());
  const [photos, setPhotos] = useState<StatusPhotoItem[]>(
    (editing?.photos || []).map((p, i) => ({ storageKey: p.storageKey, caption: p.caption ?? null, position: p.position ?? i })),
  );
  const [postToJournal, setPostToJournal] = useState<boolean>(editing?.postedToJournal ?? false);
  const [journalHeadline, setJournalHeadline] = useState<string>(editing?.journalHeadline ?? '');
  const [postToFeed, setPostToFeed] = useState<boolean>(editing?.postedToFeed ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: t('summary.statusReport.narrativePlaceholder') }),
    ],
    content: editing?.narrativeHtml || '',
  });

  useEffect(() => () => editor?.destroy(), [editor]);

  const handleSave = useCallback(async () => {
    setError(null);
    const narrative = (editor?.getHTML() || '').trim();
    if (!(editor?.getText() || '').trim()) {
      setError(t('summary.statusReport.errorEmpty'));
      return;
    }
    setSaving(true);
    try {
      const photosBody = photos.map((p, i) => ({
        storageKey: p.storageKey,
        caption: p.caption ?? null,
        position: i,
      }));
      /* DEV-1297 + DEV-1463: resolve the feed-syndication level from the
       * project's visibility.
       *
       * Full-project visibility is now a REAL DB column (migration 275) on the
       * three-value standard, replacing DEV-1039's public|private +
       * public_scope pair. The old mapping read that pair:
       *     visibility!=='public'        -> 'none'
       *     public_scope==='platform'    -> 'public'   (fans out to the geo scope)
       *     public_scope==='within_community' -> 'org'
       *
       * The ladder collapses that losslessly:
       *     team_only  -> 'none'    (nothing to syndicate — nobody can see it)
       *     community  -> 'org'     (owning org only)
       *     public     -> 'public'  (FeedSyndicationService fans out to the geo
       *                              scope ONLY at this level)
       *
       * `is_hidden` (Lockdown) still overrides everything.
       */
      const ov = getProjectOverride(projectGuid);
      const vis = ov?.visibility ?? 'team_only';
      const feedVisibility: 'none' | 'org' | 'public' =
        !ov || ov.is_hidden || vis === 'team_only'
          ? 'none'
          : vis === 'public' ? 'public' : 'org';

      const journalFields = {
        postToJournal,
        journalHeadline: postToJournal ? journalHeadline.trim() || null : null,
        postToFeed: postToJournal ? postToFeed : false,
        feedVisibility,
      };
      if (isEdit && editing) {
        await updateProjectStatusReport(projectGuid, editing.statusReportGuid, {
          narrativeHtml: narrative,
          photos: photosBody,
          ...journalFields,
        });
      } else {
        await createProjectStatusReport(projectGuid, {
          reportingDate,
          narrativeHtml: narrative,
          photos: photosBody,
          ...journalFields,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.body?.error || err?.message || t('summary.statusReport.errorSave'));
    } finally {
      setSaving(false);
    }
  }, [editor, photos, postToJournal, journalHeadline, postToFeed, isEdit, editing, projectGuid, reportingDate, onSaved, onClose, t]);

  const handleDelete = useCallback(async () => {
    if (!isEdit || !editing) return;
    if (!window.confirm(t('summary.statusReport.confirmRemove'))) return;
    setSaving(true);
    try {
      await deleteProjectStatusReport(projectGuid, editing.statusReportGuid);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.body?.error || err?.message || t('summary.statusReport.errorSave'));
      setSaving(false);
    }
  }, [isEdit, editing, projectGuid, onSaved, onClose, t]);

  const tb = useMemo(() => {
    if (!editor) return null;
    const btn = (active: boolean, label: string, fn: () => void) => (
      <button
        type="button"
        className={active ? `${styles.tbBtn} ${styles.tbBtnOn}` : styles.tbBtn}
        onMouseDown={(e) => { e.preventDefault(); fn(); }}
      >
        {label}
      </button>
    );
    return (
      <div className={styles.toolbar}>
        {btn(editor.isActive('bold'), 'B', () => editor.chain().focus().toggleBold().run())}
        {btn(editor.isActive('italic'), 'I', () => editor.chain().focus().toggleItalic().run())}
        {btn(editor.isActive('bulletList'), '≣', () => editor.chain().focus().toggleBulletList().run())}
        {btn(editor.isActive('blockquote'), '❝', () => editor.chain().focus().toggleBlockquote().run())}
      </div>
    );
  }, [editor]);

  return createPortal(
    <div className={styles.scrim} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <h3>{isEdit ? t('summary.statusReport.editTitle') : t('summary.statusReport.newTitle')}</h3>
          <button type="button" className={styles.x} onClick={onClose}>×</button>
        </div>

        <div className={styles.body}>
          <div className={styles.field}>
            <label>{t('summary.statusReport.dateLabel')}</label>
            <input
              type="date"
              className={styles.dateInput}
              value={reportingDate}
              max={todayIso()}
              disabled={isEdit}
              onChange={(e) => setReportingDate(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label>{t('summary.statusReport.narrativeLabel')}</label>
            {tb}
            <EditorContent editor={editor} className={styles.editor} />
          </div>

          <div className={styles.field}>
            <label>{t('summary.statusReport.photosLabel')}</label>
            <StatusPhotoStrip mode="write" photos={photos} onChange={setPhotos} maxPhotos={6} />
          </div>

          <div className={styles.shareBlock}>
            <label className={styles.toggleRow}>
              <input type="checkbox" checked={postToJournal} onChange={(e) => setPostToJournal(e.target.checked)} />
              <span>{t('summary.statusReport.postToJournal')}</span>
            </label>
            {postToJournal && (
              <>
                <input
                  type="text"
                  className={styles.headlineInput}
                  value={journalHeadline}
                  onChange={(e) => setJournalHeadline(e.target.value)}
                  placeholder={t('summary.statusReport.journalHeadlinePlaceholder')}
                />
                <label className={styles.toggleRow}>
                  <input type="checkbox" checked={postToFeed} onChange={(e) => setPostToFeed(e.target.checked)} />
                  <span>{t('summary.statusReport.postToFeed')}</span>
                </label>
              </>
            )}
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.foot}>
          {isEdit && (
            <button type="button" className={styles.delete} onClick={handleDelete} disabled={saving}>
              {t('summary.statusReport.remove')}
            </button>
          )}
          <div className={styles.footRight}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={saving}>
              {t('summary.statusReport.cancel')}
            </button>
            <button type="button" className={styles.save} onClick={handleSave} disabled={saving}>
              {saving ? t('summary.statusReport.saving') : isEdit ? t('summary.statusReport.saveChanges') : t('summary.statusReport.post')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
